import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { AGENT_RED_MINUTES, type AtlasEntryState } from '@infras/shared-types';
import { STATE_LABEL, STATE_TITLE } from '../../lib/atlas';
import { timeAgo } from '../../lib/agentCommands';
import {
  ATLAS_MAX_ADDRESSES_PER_RUN,
  isPreTicked,
  REMOVE_AGENTS_CONFIRM,
  REMOVE_AGENTS_PREVIEW,
  refusalLabel,
  type AgentRemovalLink,
  type AgentRemovalResult,
  type AgentRemovalTarget,
} from '../../lib/agentLifecycle';

/**
 * Remove an agent — preview → confirm → cascade → settle (BA §3).
 *
 * ROOT ONLY, the same gate as `pruneAtlasOrphans`: it is the same third-party delete,
 * against the same production access list, so it is the same person. The caller must
 * not render the control for anyone else; the server refuses it either way.
 *
 * THE PREVIEW IS THE "ATLAS FOOTPRINT" PANEL. There is no second read query for it,
 * deliberately: a preview assembled from a different code path is a preview of
 * something other than what will be deleted. Opening this dialog is how an operator
 * sees what an agent owns, and it writes nothing until Remove is pressed.
 *
 * PRE-UNTICKED IS THE SAFETY FEATURE (AC-7). Selection keys on the server's
 * `removable` — never on `state` alone. Whatever the server refuses
 * arrives unticked, with the refusal in words beside it. Tick one anyway and the fresh
 * re-classification inside the confirm call refuses it again (AC-10). Two independent
 * stops, on purpose.
 *
 * THE SHARED-ADDRESS COLUMN IS GONE (owner decision, Binh, 2026-08-25: "don't care
 * about it. We never have it."). It described the NAT case — several agents behind one
 * egress address — which the live fleet has never produced: 356 agents, 356 distinct
 * addresses. It cost a table column, two warning chips and a paragraph of explanation
 * on every preview, to describe a situation nobody has seen. The server's `removable`
 * and its refusal text remain the authority, so if the case ever does arise the row is
 * still refused; it just is not pre-explained.
 *
 * BULK IS THE NORMAL CASE, not an extra. `removeAgents` has always taken a set; the
 * fleet list now names one (row ticks, or a cluster's silent agents in one click),
 * because a DOKS hop leaves a cluster's worth of dead agents at once and removing them
 * one dialog at a time is how an operator loses an evening — and how a click gets
 * swallowed by the list re-sorting under the cursor between runs.
 *
 * NO CLIENT-SIDE CHUNKING, deliberately. The server's cap is ATLAS_MAX_ADDRESSES_PER_RUN
 * (renamed from AGENT_CASCADE_MAX_PER_RUN on 2026-08-25 — it now bounds the cascade, the
 * prune AND the restore, which is why it no longer carries the cascade's name)
 * per PROJECT per run and the excess comes back as `deferred_cap` — refused, named,
 * and re-offered by the next preview. Splitting the set here would mean firing several
 * production deletes from one click, each classified against a different read of Atlas,
 * with no single result panel to read afterwards. Instead the dialog says so before the
 * click and again in the result: run it again on what is left. One click, one run, one
 * answer.
 *
 * "Remove" is RECLAIM ONLY (owner decision, 2026-08-24): the agent's addresses come
 * off Atlas and the agent is marked removed — soft, never deleted, because the link
 * row is the ownership history. There is no block list and no evict state.
 *
 * BUT RECLAIM-ONLY IS NOT THE SAME AS REVERSIBLE, and this dialog must not imply it
 * is. `atlasHealthy` is sticky-true on the agent (`agent.ts:218` vs the re-register
 * gate at `:275`), and the heartbeat's `success:false` answer is not acted on by the
 * shipped binary — so a node that is ALREADY whitelisted never re-registers, and
 * nothing re-adds an address taken off it. `docs/atlas-whitelist.md` says so in as
 * many words: restore is the only repair path. Removing a still-reporting node's
 * address therefore cuts its pods off from the database until a human puts it back
 * (Restore on Cluster access, then Reconcile now). Those rows are called out and
 * start UNTICKED, exactly like a shared one.
 */
export function AgentRemoveDialog({
  agentIds,
  label,
  onClose,
}: {
  /** Bulk-ready by construction — the mutation takes a set; the fleet list drives one
   *  agent at a time today because it has no multi-select. */
  agentIds: string[];
  /** What to call the subject in the heading — a node name reads better than an id. */
  label: string;
  onClose: () => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<AgentRemovalResult | null>(null);

  const [preview, previewState] = useMutation<{ removeAgents: AgentRemovalResult }>(
    REMOVE_AGENTS_PREVIEW,
    {
      onCompleted: (d) => {
        // Tick exactly what the cascade would take, and only that: `removable` is the
        // server's verdict.
        //
        // ...minus the rows whose OWN node is still reporting. The server permits those
        // deliberately (reclaim-only: an agent's own liveness is not a veto), but nothing
        // re-adds the address afterwards, so ticking one by default would make cutting a
        // live production node off the database the path of least resistance. Opt in.
        //
        // "Still reporting" is the SERVER'S `ownAgentLive` — NOT RED YET, i.e. silent less
        // than AGENT_RED_MINUTES. One clock, one answer: the row that is pre-ticked here,
        // the row that carries a bulk checkbox on the fleet list and the row that is red
        // there are the same row, and stay the same row if the owner moves the threshold
        // again. It used to be derived from the retired 4 h reclaim clock, which called a
        // node dead for 20 minutes alive: on 2026-08-24 eight provably deleted droplets
        // arrived unticked behind a red warning, and the operator had to override it
        // eight times. Per LINK, not per agent — one agent's links are all its own node,
        // but the field is the link's to answer.
        const next = new Set<string>();
        for (const a of d.removeAgents.agents) {
          for (const l of a.links) if (isPreTicked(l)) next.add(key(l));
        }
        setTicked(next);
      },
    },
  );

  const [confirm, confirmState] = useMutation<{ removeAgents: AgentRemovalResult }>(
    REMOVE_AGENTS_CONFIRM,
    {
      // The removal invalidates both surfaces behind this dialog — the fleet's agent
      // status and every Atlas count on the screen underneath. Wait for them, so
      // dismissing the result never puts the operator back on a cached row that still
      // offers to remove what is already gone (the bug `AddressDrawer` records).
      refetchQueries: ['Agents', 'AtlasEgressIps', 'AtlasProjectsOverview', 'NavCounts', 'AgentChecklist'],
      awaitRefetchQueries: true,
      onCompleted: (d) => setResult(d.removeAgents),
    },
  );

  useEffect(() => {
    void preview({ variables: { agentIds } });
    // The dialog's subject is fixed for its lifetime, so this runs once on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    cardRef.current?.focus();
    // CAPTURE phase, and it stops the event. This dialog renders INSIDE the fleet
    // drawer, which closes itself on Escape too — bubbling, one Escape dismissed the
    // confirm and navigated the drawer away behind it, so an operator who changed
    // their mind lost the list they were working through. Handling it first, and only
    // here, closes exactly one layer.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || confirmState.loading) return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, confirmState.loading]);

  const agents = previewState.data?.removeAgents.agents ?? [];
  const links = useMemo(() => agents.flatMap((a) => a.links), [agents]);
  // The owning agent's last heartbeat. It is the operator's PRIMARY EVIDENCE now that
  // there is one clock and no second one to appeal to, so it renders on EVERY row —
  // not only in the per-agent heading, which appears solely in a multi-agent preview.
  const ownerById = useMemo(() => new Map(agents.map((a) => [a.agentId, a])), [agents]);
  const heartbeatOf = (l: AgentRemovalLink) => ownerById.get(l.agentId)?.lastSeenAt;
  // "Is the node I am about to cut off still running?" — the server's answer, on the
  // one clock this app has, the same one the fleet list's bulk checkboxes key on. See
  // `ownAgentLive` in agentLifecycle.ts.
  const stillReporting = (l: AgentRemovalLink) => l.ownAgentLive;
  const liveOwn = links.filter((l) => stillReporting(l)).length;
  const targets = useMemo<AgentRemovalTarget[]>(
    () =>
      links
        .filter((l) => ticked.has(key(l)))
        .map((l) => ({
          agentId: l.agentId,
          atlasProjectKey: l.atlasProjectKey,
          ipCidr: l.ipCidr,
        })),
    [links, ticked],
  );
  // Which projects the ticked set would push past the per-run cap. Bulk removal is
  // the case this exists for.
  const overCap = useMemo(() => {
    const perProject = new Map<string, number>();
    for (const t of targets)
      perProject.set(t.atlasProjectKey, (perProject.get(t.atlasProjectKey) ?? 0) + 1);
    return [...perProject.entries()]
      .filter(([, n]) => n > ATLAS_MAX_ADDRESSES_PER_RUN)
      .map(([k]) => k);
  }, [targets]);

  const toggle = (k: string) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  return (
    <div
      className="confirm-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !confirmState.loading) onClose();
      }}
    >
      <div
        ref={cardRef}
        className="confirm-card atlas-prune-card agent-remove-card"
        role="dialog"
        aria-modal="true"
        aria-label={`Remove agent ${label}`}
        tabIndex={-1}
      >
        <h3 className="confirm-title">Remove {label}?</h3>

        {result ? (
          <RemovalResult result={result} onClose={onClose} />
        ) : (
          <>
            <p className="confirm-body">
              Removing an agent reclaims the Atlas addresses it put on the lists below, and marks
              the agent removed. Nothing is blocked and nothing else about the cluster changes —
              but nothing re-adds an address either: a node that is already whitelisted never
              re-registers, so if you take an address a running node is using, its pods lose the
              database until someone restores it from Cluster access and reconciles.
            </p>

            <h4 className="section">
              Atlas footprint
              {agents.length > 1 && (
                <span className="muted">
                  {' '}
                  — {agents.length} agents, {links.length} address
                  {links.length === 1 ? '' : 'es'}
                </span>
              )}
            </h4>
            {previewState.loading ? (
              <p className="muted">Reading what this agent owns…</p>
            ) : previewState.error ? (
              <>
                <p className="error">Couldn&apos;t build the preview — the API didn&apos;t respond.</p>
                <p className="muted">
                  Nothing was removed. The preview is the only way in, so there is no half-done
                  state to clean up — close this and try again.
                </p>
              </>
            ) : !links.length ? (
              <p className="muted">
                {agents.length === 1 ? 'This agent holds' : `These ${agents.length} agents hold`} no
                Atlas access-list address, so there is nothing to reclaim.{' '}
                {agents.length === 1 ? 'Removing it' : 'Removing them'} would only mark{' '}
                {agents.length === 1 ? 'the agent' : 'them'} removed.
              </p>
            ) : (
              <>
                {/* The preview can be several projects wide inside a dialog that is
                    not. It scrolls in its own box rather than pushing the card past
                    the viewport — a confirm whose buttons are off-screen. */}
                <div className="agent-remove-preview">
                  <table className="atlas-table">
                    <thead>
                      <tr>
                        <th title="Untick a row to leave that address on Atlas">Reclaim</th>
                        <th>Atlas project</th>
                        <th>Address</th>
                        <th>Entry state</th>
                        <th title="The owning node's last heartbeat, and why this row is ticked or not">
                          Last heartbeat
                        </th>
                      </tr>
                    </thead>
                    {/* One tbody per AGENT, not one flat list of addresses: a flat
                        table would give the operator no way to tell which agent's leg
                        is which — which is also exactly what `confirm` names:
                        (agent, project, address). */}
                    {agents.map((a) => (
                      <tbody key={a.agentId} className="agent-remove-agent">
                        {agents.length > 1 && (
                          <tr className="agent-remove-agenthead">
                            <td colSpan={5}>
                              <strong>{a.nodeName || a.agentId}</strong>{' '}
                              <span className="muted">
                                {a.clusterName} · last heartbeat {timeAgo(a.lastSeenAt)} ·{' '}
                                {a.links.length} address{a.links.length === 1 ? '' : 'es'}
                              </span>
                            </td>
                          </tr>
                        )}
                        {!a.links.length && (
                          <tr>
                            <td colSpan={5} className="muted">
                              Holds no Atlas access-list address — it will only be marked removed.
                            </td>
                          </tr>
                        )}
                        {a.links.map((l) => {
                        const k = key(l);
                        return (
                          <tr key={k}>
                            <td>
                              <input
                                type="checkbox"
                                checked={ticked.has(k)}
                                onChange={() => toggle(k)}
                                aria-label={`Remove ${l.ipCidr} from ${l.atlasProjectKey}`}
                              />
                            </td>
                            <td>
                              {l.projectDisplayName || l.atlasProjectKey}
                              <div className="atlas-sub muted">{l.atlasProjectKey}</div>
                            </td>
                            <td className="mono">{l.ipCidr}</td>
                            <td>
                              <LinkState link={l} />
                            </td>
                            <td>
                              {/* THE AGE IS ON EVERY ROW, whatever the verdict. It is
                                  the operator's primary evidence now that one clock
                                  decides everything, and the per-agent heading that
                                  used to carry it renders only in a multi-agent
                                  preview — so a single-node preview of a node silent
                                  16 minutes arrived ticked with no age anywhere in
                                  the dialog. A default this destructive does not get
                                  to hide its evidence. */}
                              <div className="agent-remove-age">
                                {timeAgo(heartbeatOf(l))}
                              </div>
                              {!l.removable ? (
                                // Never leave a row unticked without saying why — an
                                // operator who cannot see the reason looks for another way.
                                <span className="atlas-sub" title={l.refusal ?? undefined}>
                                  not removable{l.refusal ? ` — ${refusalLabel(l.refusal)}` : ''}
                                </span>
                              ) : stillReporting(l) ? (
                                // THIS node has not gone silent yet. The server allows
                                // removing it anyway (reclaim-only) and will not stop
                                // you; nothing puts the address back either. Unticked,
                                // and said out loud.
                                <span
                                  className="badge failed"
                                  title={`This node heartbeat less than ${AGENT_RED_MINUTES} min ago, so it is still reporting. Removing its address takes its pods off the database, and nothing re-adds it: a whitelisted node never re-registers. Restore it from Cluster access if you do this by mistake.`}
                                >
                                  still reporting
                                </span>
                              ) : (
                                // THE PRE-TICKED ROW. `isPreTicked` fires as soon as the
                                // node goes red, and the age above is the evidence for it.
                                <span className="atlas-sub muted">
                                  silent past {AGENT_RED_MINUTES} min — ticked
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                        })}
                      </tbody>
                    ))}
                  </table>
                </div>

                {overCap.length > 0 && (
                  // The cap is per project per run, and the server defers the excess
                  // as `deferred_cap` rather than dropping it. Saying so BEFORE the
                  // click is the difference between "the run was bounded" and "some
                  // of what I ticked silently did not happen".
                  <p className="muted text-xs">
                    {overCap.join(', ')}: more than {ATLAS_MAX_ADDRESSES_PER_RUN} addresses are ticked
                    for one project. The run is capped at {ATLAS_MAX_ADDRESSES_PER_RUN} per project —
                    the rest come back as <span className="mono">deferred_cap</span>, are{' '}
                    <strong>not</strong> deleted, and stay ticked-able: reopen this dialog after the
                    run and confirm again to take them. Nothing is silently dropped, and nothing is
                    split into several deletes behind one click.
                  </p>
                )}

                {liveOwn > 0 && (
                  // The one case the server does NOT stop: an agent's own liveness is
                  // deliberately not a veto (reclaim-only). That decision was taken on the
                  // understanding that a live node re-registers — it does not, so this is
                  // where an operator gets told before the click rather than after.
                  //
                  // This and the cap note are the ONLY two guards the dialog explains in
                  // prose. Everything else a server refusal produces reads in the row.
                  <p className="error">
                    <strong>
                      {liveOwn} address{liveOwn === 1 ? '' : 'es'} below{' '}
                      {liveOwn === 1 ? 'belongs' : 'belong'} to a node that heartbeat in the last{' '}
                      {AGENT_RED_MINUTES} min
                    </strong>{' '}
                    and start{liveOwn === 1 ? 's' : ''} unticked. The server will not refuse
                    {liveOwn === 1 ? ' it' : ' them'} — it is your call — but nothing re-adds the
                    address: a whitelisted node never re-registers, so the pods on it lose the
                    database until someone restores the entry from Cluster access and runs
                    Reconcile now.
                  </p>
                )}

              </>
            )}

            <div className="btn-row confirm-actions">
              {/* An agent that owns NOTHING can be removed with an empty confirm — the
                  unmapped-cluster case, which the server allows precisely because there is
                  no allow-list to name. An agent that owns addresses cannot: `confirm` is
                  the allow-list, and a real run with none named is refused server-side. So
                  when everything is unticked the safe button is "Keep it", not a Remove
                  that comes back with an internal contract message. Same for a preview that
                  failed: there is nothing to act on and no evidence to act from. */}
              <button
                className="btn btn-danger"
                disabled={
                  confirmState.loading ||
                  previewState.loading ||
                  !!previewState.error ||
                  (links.length > 0 && targets.length === 0)
                }
                onClick={() => void confirm({ variables: { agentIds, confirm: targets } })}
              >
                {confirmState.loading
                  ? 'Removing…'
                  : targets.length
                    ? `Remove ${targets.length} address${targets.length === 1 ? '' : 'es'}`
                    : links.length
                      ? 'Nothing ticked'
                      : agents.length === 1
                        ? 'Remove the agent only'
                        : `Remove ${agents.length} agents`}
              </button>
              {/* "Keep it" rather than "Cancel": the action is a removal, and a dialog
                  offering Cancel beside Remove makes the safe button the ambiguous one. */}
              <button className="btn btn-ghost" disabled={confirmState.loading} onClick={onClose}>
                Keep it
              </button>
            </div>
            {confirmState.error && <p className="error">{confirmState.error.message}</p>}
          </>
        )}
      </div>
    </div>
  );
}

const key = (l: { agentId: string; atlasProjectKey: string; ipCidr: string }) =>
  `${l.agentId}|${l.atlasProjectKey}|${l.ipCidr}`;

/** Unknown is muted and worded, never green — `inLiveList` is three-valued, and "we
 *  have not looked" must not read as "not listed". */
function LinkState({ link }: { link: AgentRemovalLink }) {
  if (link.inLiveList == null) {
    return (
      <span
        className="atlas-sub"
        style={{ color: 'var(--faint)' }}
        title="This project's access list could not be read, so the state of this address in it is unknown."
      >
        unknown
      </span>
    );
  }
  if (link.inLiveList === false) return <span className="atlas-sub">not in the live list</span>;
  const state: AtlasEntryState | null | undefined = link.state as AtlasEntryState | null;
  if (!state) return <span className="atlas-sub muted">—</span>;
  return (
    <span className={`atlas-state atlas-state--${state}`} title={STATE_TITLE[state]}>
      {STATE_LABEL[state]}
    </span>
  );
}

/**
 * The outcomes, grouped — and `refused` and `failed` stay apart.
 *
 * `refused` is our own verdict that the entry should stay: the address is still on
 * Atlas and that is correct. `failed` is a delete Atlas rejected: also still on Atlas,
 * but wrong, and the operator has to retry. `not_attempted` is the rate-limit stop —
 * the batch halted, and those addresses were never tried. Folding any of them together
 * would report a broken call as a deliberate decision.
 */
function RemovalResult({ result, onClose }: { result: AgentRemovalResult; onClose: () => void }) {
  const by = (...kinds: string[]) => result.outcomes.filter((o) => kinds.includes(o.outcome));
  // `already_absent` is NOT `removed`: this run deleted nothing there — the entry had
  // gone from Atlas before we asked. Counting the two together would report work that
  // did not happen, and would hide the case where a list is being edited by someone
  // else at the same time.
  const removed = by('removed');
  const absent = by('already_absent');
  const refused = by('refused', 'not_confirmed');
  const failed = by('failed', 'not_attempted');
  // A HINT, never a gate: the cap refusal carries the server's own wording (the
  // outcome's `reason` is prose, not a code), so this only decides whether one extra
  // sentence renders. If the wording ever moves, the rows still read correctly under
  // "Refused".
  const deferred = refused.filter((o) => o.reason?.includes('per-project cap'));

  return (
    <div className={`atlas-prune-result${failed.length > 0 ? ' atlas-prune-result--failed' : ''}`}>
      {/* Everything that grows with the run scrolls; `Done` stays outside it. A bulk
          result is as tall as the bulk preview was, and Done is the only way out. */}
      <div className="agent-remove-outcomes">
        <strong>
        {removed.length} removed
        {absent.length > 0 && ` · ${absent.length} already absent`}
        {refused.length > 0 && ` · ${refused.length} refused`}
        {failed.length > 0 && ` · ${failed.length} failed`}
      </strong>

      {/* The agent's own terminal status, and it is written only after Atlas answered
          for every address. `removal_failed` with an address still held is the state
          that must not read as "handled". */}
      {result.settled.map((s) => (
        <p key={s.agentId} className={s.status === 'removal_failed' ? 'error' : 'muted'}>
          {s.agentId} is now <strong>{s.status}</strong>
          {s.reason ? ` — ${s.reason}` : ''}
        </p>
      ))}

      {failed.length > 0 && (
        <details open>
          <summary>Failed at Atlas — still whitelisted, retry</summary>
          <ul>
            {failed.map((f) => (
              <li key={key(f)}>
                <span className="mono">{f.ipCidr}</span> in {f.atlasProjectKey} — {f.outcome}
                {f.reason ? `: ${f.reason}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
      {deferred.length > 0 && (
        // The one refusal that is not a verdict about the address: the run was
        // bounded, nothing is wrong, and the work is simply not finished. Say what to
        // do about it, because "refused" alone reads as "we decided not to".
        <p className="muted">
          {deferred.length} address{deferred.length === 1 ? '' : 'es'} hit the{' '}
          {ATLAS_MAX_ADDRESSES_PER_RUN}-per-project cap and{' '}
          {deferred.length === 1 ? 'was' : 'were'} not attempted. Nothing is lost — close this,
          open Remove again on the same agents and confirm to take the rest.
        </p>
      )}
      {refused.length > 0 && (
        <details open>
          <summary>Refused — kept on purpose</summary>
          <ul>
            {refused.map((f) => (
              <li key={key(f)} title={f.reason ?? undefined}>
                <span className="mono">{f.ipCidr}</span> in {f.atlasProjectKey} —{' '}
                {refusalLabel(f.reason) || f.outcome}
              </li>
            ))}
          </ul>
        </details>
      )}
      {removed.length > 0 && (
        <details>
          <summary>Removed from Atlas</summary>
          <ul>
            {removed.map((f) => (
              <li key={key(f)}>
                <span className="mono">{f.ipCidr}</span> in {f.atlasProjectKey}
              </li>
            ))}
          </ul>
        </details>
      )}
      {absent.length > 0 && (
        <details>
          <summary>Already absent — this run deleted nothing there</summary>
          <ul>
            {absent.map((f) => (
              <li key={key(f)}>
                <span className="mono">{f.ipCidr}</span> in {f.atlasProjectKey} — the entry was no
                longer in the list when we asked. The link row is still released.
              </li>
            ))}
          </ul>
        </details>
      )}
      {result.unknownAgentIds.length > 0 && (
          <p className="error">
            Not found: {result.unknownAgentIds.join(', ')} — nothing was done for{' '}
            {result.unknownAgentIds.length === 1 ? 'it' : 'them'}.
          </p>
        )}
      </div>

      <div className="btn-row confirm-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}
